import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Button,
  Nav,
  NavItem,
  NavLink,
  Input,
  Label,
  FormGroup,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner
} from "reactstrap";
import classnames from "classnames";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import axios from "axios";
import BASE_URL from "../../config";
import Select from "react-select";
import { 
  hasEditOrHigherPermission, 
  hasPagePermissionWithAdmin,
  isUserAdmin,
  hasViewOnlyPermission
} from "../../components/Permission/PermissionService";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { getTicketLogs, markTicketNotificationAsRead, getTicketDocuments } from "../../components/Api/GmsTicket";
import Pagination from "rc-pagination";
import 'rc-pagination/assets/index.css';

// Components
import { getRooms } from "../../components/Api/GmsRoom";
import { getEquipments } from "../../components/Api/GmsEquipment";
import { searchEmployees } from "../../components/Api/GmsEmployee";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";

const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
};

const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return {
    userId: authUser.userId || authUser.user_Id || 1,
    userName: authUser.userName || "System",
    companyId: authUser.companyId || user.user_Company_Id || null,
    branchId: authUser.branchId || user.user_Branch_Id || null,
    employeeId: authUser.employeeId || user.user_EmployeeId || null,
    isAdmin: authUser.isAdmin || false
  };
};

const formatDate = (date) => {
  if (!date) return "-";
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

const GmsMaintenanceTicketsReportPage = (props) => {
  const session = useMemo(() => getUserSession(), []);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Data State
  const [tickets, setTickets] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [loading, setLoading] = useState(false);

  // Tab State
  const [activeMainTab, setActiveMainTab] = useState("1"); // 1: Assigned to Me, 2: Assigned by Me
  const [activeCategoryTab, setActiveCategoryTab] = useState("active"); // active, resolved
  const [activeStatusTab, setActiveStatusTab] = useState("any");

  // Filter State
  const [filters, setFilters] = useState({
    businessId: null,
    branchId: null,
    roomId: null,
    equipmentId: null,
    priority: null,
    assignee: null,
    assignedDate: "",
    resolvedDate: ""
  });
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // Modal State
  const [modal, setModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [updateData, setUpdateData] = useState({
    statusId: 1,
    comment: '',
    assignedTo: null
  });
  const [ticketLogs, setTicketLogs] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);

  // Technician Search
  const [technicianOptions, setTechnicianOptions] = useState([]);
  const [techLoading, setTechLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const pageRoute = '/GmsMaintenanceTicketsReport';
  const hasPermission = hasPagePermissionWithAdmin(permissions, pageRoute);
  const isEditUser = isUserAdmin() || hasEditOrHigherPermission(permissions, pageRoute);

  const statusList = [
    { value: 1, label: 'Open', color: 'danger', category: 'active' },
    { value: 2, label: 'Assigned', color: 'info', category: 'active' },
    { value: 8, label: 'In Progress', color: 'primary', category: 'active' },
    { value: 3, label: 'OnHold', color: 'warning', category: 'active' },
    { value: 5, label: 'Reopened', color: 'primary', category: 'active' },
    { value: 7, label: 'Done', color: 'success', category: 'active' },
    { value: 6, label: 'Closed', color: 'success', category: 'resolved' },
    { value: 4, label: 'Cancelled', color: 'secondary', category: 'resolved' }
  ];

  const priorityOptions = [
    { value: 0, label: 'Critical', color: 'danger' },
    { value: 1, label: 'High', color: 'warning' },
    { value: 2, label: 'Medium', color: 'info' },
    { value: 3, label: 'Low', color: 'secondary' }
  ];

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (filters.businessId && filters.branchId) {
      setPage(1);
      loadTickets(1);
    }
  }, [filters, activeMainTab, activeCategoryTab, activeStatusTab, searchParams]);

  useEffect(() => {
    if (filters.businessId && filters.branchId) {
      loadTickets(page);
    }
  }, [page]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [busRes, roomsRes, equipRes] = await Promise.all([
        getBusinesses({ business_Active: true }),
        getRooms({ Status: true }),
        getEquipments({ status: true })
      ]);

      setBusinesses(busRes.data || busRes || []);
      setRooms((roomsRes.data || roomsRes || []).map(r => ({ value: r.room_Id, label: r.room_Name_English })));
      setEquipments((equipRes.data || equipRes || []).map(e => ({ value: e.eq_Id || e.equipment_Id, label: e.eq_Name_English || e.equipment_Name_English })));

      // Session logic
      let initialBusId = session.companyId ? (typeof session.companyId === 'string' ? parseInt(session.companyId) : session.companyId) : null;
      let initialBrId = session.branchId ? (typeof session.branchId === 'string' ? parseInt(session.branchId) : session.branchId) : null;

      if (initialBusId) await loadBranches(initialBusId);
      
      setFilters(prev => ({ ...prev, businessId: initialBusId, branchId: initialBrId }));

    } catch (error) {
      console.error("Error loading setup data", error);
    } finally {
      setLoading(false);
    }
  };

  const loadBranches = async (busId) => {
    try {
      const res = await getBranches({ Business_Id: busId, branch_Active: true });
      setBranches(res.data || res || []);
    } catch (e) { console.error(e); }
  };

  const loadTickets = async (pageNum = page) => {
    if (!filters.businessId || !filters.branchId) return;
    try {
      setLoading(true);
      const authHeader = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };
      
      let statusIdParam = null;
      if (activeStatusTab !== "any") {
         statusIdParam = parseInt(activeStatusTab);
      }

      let isActiveParam = null;
      if (activeStatusTab === "any") {
         if (activeCategoryTab === "active") isActiveParam = true;
         if (activeCategoryTab === "resolved") isActiveParam = false;
      }

      const payload = { 
        BusinessID: filters.businessId,
        BranchID: filters.branchId,
        PageNumber: pageNum,
        PageSize: 10,
        IsActive: isActiveParam,
        StatusID: statusIdParam,
        RoomID: filters.roomId?.value || null,
        EquipmentID: filters.equipmentId?.value || null,
        Priority: filters.priority?.value ?? null,
        AssignedTo: filters.assignee?.value || null,
        AssignedDate: filters.assignedDate || null,
        ResolvedDate: filters.resolvedDate || null
      };

      // Tab Filtering Logic
      if (activeMainTab === "1") {
        payload.AssignedTo = session.employeeId;
      } else {
        payload.CreatedBy = session.userId;
      }
      
      const ticketIdParam = searchParams.get("ticketID");
      const ticketCodeParam = searchParams.get("ticketCode");
      // Note: Backend might need specific handling if filtering by specific code. 
      // Assuming frontend URL param was mainly a focus mechanism.

      const response = await axios.post(`${BASE_URL}/api/Ticket/get`, payload, authHeader);
      const data = response.data || [];
      setTickets(data);
      if (data.length > 0) {
         setTotalRecords(data[0].totalRecords || data[0].TotalRecords || 0);
      } else {
         setTotalRecords(0);
      }
    } catch (error) {
      console.error("Error loading tickets", error);
    } finally {
      setLoading(false);
    }
  };

  const searchTechs = (inputValue, { action }) => {
    if (action === 'input-change') {
      if (searchTimeout) clearTimeout(searchTimeout);
      const timeout = setTimeout(async () => {
        if (!inputValue || inputValue.length < 3) return;
        try {
          setTechLoading(true);
          const res = await searchEmployees(inputValue, 1, 30, filters.businessId, filters.branchId);
          const options = (res.items || []).map(e => ({ 
            value: e.employee_Id, 
            label: `${e.employee_NameEn || 'Unknown'} (${e.emp_No || 'N/A'})` 
          }));
          setTechnicianOptions(options);
        } catch (e) { 
           console.error("Search error", e); 
        } finally { 
           setTechLoading(false); 
        }
      }, 400);
      setSearchTimeout(timeout);
    }
  };

  const getStatusBadge = (s) => {
    const opt = statusList.find(o => o.value === s);
    return <Badge color={`soft-${opt?.color || "secondary"}`} pill className={`text-${opt?.color || "secondary"} px-2 py-1`}>{opt?.label || "Unknown"}</Badge>;
  };

  const getPriorityBadge = (p) => {
    const opt = priorityOptions.find(o => o.value === p);
    return <Badge color={`soft-${opt?.color || "secondary"}`} className={`text-${opt?.color || "secondary"} px-2 py-1`}>{opt?.label || "Unknown"}</Badge>;
  };

  const filteredTickets = tickets;

  const handleExport = () => {
    if (filteredTickets.length === 0) return toast.info("No data to export");
    
    const exportData = filteredTickets.map(t => ({
      Code: `#${t.ticketCode}`,
      "Room/Equip": t.roomName || t.equipmentName || "-",
      Priority: priorityOptions.find(o => o.value === t.priority)?.label || "-",
      Status: statusList.find(o => o.value === t.statusId)?.label || "-",
      Assignee: t.assignedToName || "Unassigned",
      "Issue Description": t.description,
      "Assigned Date": t.assignedDate ? formatDate(t.assignedDate) : "-",
      "Resolved Date": t.resolvedDate ? formatDate(t.resolvedDate) : "-"
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "MaintenanceTickets");
    XLSX.writeFile(wb, `MaintenanceTickets_Report_${new Date().getTime()}.xlsx`);
  };

  const clearUrlFilter = () => {
    setSearchParams({});
  };

  const handleActionClick = (ticket, mode) => {
    if (mode === 'edit') {
       toggleModal(ticket);
    } else if (mode === 'view') {
       toggleViewModal(ticket);
    }
  };

  const toggleModal = (ticket) => {
    if (ticket) {
      setSelectedTicket(ticket);
      setUpdateData({
        statusId: ticket.statusId,
        comment: '',
        assignedTo: ticket.assignedTo ? { value: ticket.assignedTo, label: ticket.assignedToName } : null
      });
    }
    setModal(!modal);
  };

  const toggleViewModal = async (ticket) => {
    if (ticket) {
      setSelectedTicket(ticket);
      setViewLoading(true);
      setViewModal(true);
      setTicketLogs([]); // Reset logs
      try {
        const logs = await getTicketLogs(ticket.ticketID || ticket.ticketId);
        setTicketLogs(logs || []);

        // Mark unread logs as read
        const userId = session.userId;
        const employeeId = session.employeeId;
        const unreadLogs = (logs || []).filter(l => 
          l.isRead === false && 
          (l.notificationRecipientID === userId || l.notificationRecipientID === employeeId)
        );

        for (const log of unreadLogs) {
          try {
            await markTicketNotificationAsRead(log.logID, log.notificationRecipientID);
          } catch (e) {
             console.error("Error marking as read", e);
          }
        }
      } catch (error) {
        console.error("Error loading logs", error);
        toast.error("Failed to load ticket history");
      } finally {
        setViewLoading(false);
      }
    } else {
      setViewModal(false);
    }
  };

  const handleUpdateTicket = async () => {
    try {
      setLoading(true);
      const authHeader = { headers: { 
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        'Content-Type': 'multipart/form-data'
      } };

      const updateFormData = new FormData();
      updateFormData.append('ticketID', selectedTicket.ticketID || selectedTicket.ticketId);
      updateFormData.append('statusID', updateData.statusId);
      updateFormData.append('comment', updateData.comment);
      
      const isActuallyAdmin = session.isAdmin || isUserAdmin();
      const finalAssignedTo = isActuallyAdmin ? (updateData.assignedTo?.value || selectedTicket.assignedTo || '') : (selectedTicket.assignedTo || '');

      if (!finalAssignedTo && updateData.statusId !== 1 && updateData.statusId !== 4) {
         toast.warning("A ticket must have an assigned technician for statuses other than Open or Cancelled.");
         setLoading(false);
         return;
      }
      
      updateFormData.append('assignedTo', finalAssignedTo);
      updateFormData.append('businessId', filters.businessId);
      updateFormData.append('branchId', filters.branchId || '');
      updateFormData.append('createdBy', session.userId);
      updateFormData.append('priority', selectedTicket.priority);
      updateFormData.append('description', selectedTicket.description);
      updateFormData.append('ticketType', selectedTicket.ticketType);

      await axios.post(`${BASE_URL}/api/Ticket/save`, updateFormData, authHeader);
      toast.success("Ticket updated successfully");
      setModal(false);
      loadTickets();
    } catch (error) {
      console.error("Error updating ticket", error);
      toast.error("Failed to update ticket");
    } finally {
      setLoading(false);
    }
  };

  const handleViewDocument = async (ticket) => {
    try {
      const docs = await getTicketDocuments(ticket.ticketID || ticket.ticketId);
      if (docs && docs.length > 0) {
        window.open(`${BASE_URL}${docs[0].documentPath}`, '_blank');
      } else {
        toast.info("No document found for this ticket.");
      }
    } catch (error) {
      console.error("Error loading documents", error);
      toast.error("Failed to load document path");
    }
  };

  if (!hasPermission) return <div className="page-content"><Container fluid><h4>Access Denied</h4></Container></div>;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Maintenance" breadcrumbItem="Tickets Report" />

          {(searchParams.get("ticketID") || searchParams.get("ticketCode")) && (
              <div className="alert alert-info d-flex justify-content-between align-items-center mb-3">
                  <span>Viewing specific ticket from notification focus.</span>
                  <Button size="sm" color="info" onClick={clearUrlFilter}>Show All Tickets</Button>
              </div>
          )}

          {/* Top Selection */}
          <Card className="shadow-sm mb-4">
             <CardBody className="py-2">
                <Row className="gx-3 align-items-center">
                   <Col md={6}>
                      <div className="d-flex align-items-center">
                         <Label className="me-2 mb-0 fw-bold">Company</Label>
                         <Select 
                           value={businesses.map(b => ({value: b.business_Id, label: b.business_NameEn})).find(o => o.value === filters.businessId)}
                           onChange={opt => { 
                             setFilters({...filters, businessId: opt?.value, branchId: null}); 
                             loadBranches(opt?.value);
                           }}
                           options={businesses.map(b => ({value: b.business_Id, label: b.business_NameEn}))}
                           isDisabled={!!session.companyId}
                           styles={{...selectStyles, container: b => ({...b, flexGrow: 1})}}
                         />
                      </div>
                   </Col>
                   <Col md={6}>
                      <div className="d-flex align-items-center">
                         <Label className="me-2 mb-0 fw-bold">Branch</Label>
                         <Select 
                           value={branches.map(b => ({value: b.branch_Id, label: b.branch_NameEn})).find(o => o.value === filters.branchId)}
                           onChange={opt => setFilters({...filters, branchId: opt?.value})}
                           options={branches.map(b => ({value: b.branch_Id, label: b.branch_NameEn}))}
                           isDisabled={!!session.branchId || !filters.businessId}
                           styles={{...selectStyles, container: b => ({...b, flexGrow: 1})}}
                         />
                      </div>
                   </Col>
                </Row>
             </CardBody>
          </Card>

          {/* Filters Row */}
          <Card className="shadow-sm border-0 mb-4 bg-soft-light">
             <CardBody className="p-3">
                <Row className="gx-3 align-items-end">
                   <Col md={2}>
                      <FormGroup className="mb-2">
                         <Label className="small fw-bold text-muted mb-1">Assigned Date</Label>
                         <Input type="date" className="form-control" style={{ height: '38px' }} value={filters.assignedDate} onChange={e => setFilters({...filters, assignedDate: e.target.value})} />
                      </FormGroup>
                   </Col>
                   <Col md={2}>
                      <FormGroup className="mb-2">
                         <Label className="small fw-bold text-muted mb-1">Resolved Date</Label>
                         <Input type="date" className="form-control" style={{ height: '38px' }} value={filters.resolvedDate} onChange={e => setFilters({...filters, resolvedDate: e.target.value})} />
                      </FormGroup>
                   </Col>
                   <Col md={2}>
                      <FormGroup className="mb-2">
                         <Label className="small fw-bold text-muted mb-1">Room</Label>
                         <Select isClearable options={rooms} styles={selectStyles} value={filters.roomId} onChange={v => setFilters({...filters, roomId: v})} />
                      </FormGroup>
                   </Col>
                   <Col md={2}>
                      <FormGroup className="mb-2">
                         <Label className="small fw-bold text-muted mb-1">Equipment</Label>
                         <Select isClearable options={equipments} styles={selectStyles} value={filters.equipmentId} onChange={v => setFilters({...filters, equipmentId: v})} />
                      </FormGroup>
                   </Col>
                   <Col md={2}>
                      <FormGroup className="mb-2">
                         <Label className="small fw-bold text-muted mb-1">Priority</Label>
                         <Select isClearable options={priorityOptions} styles={selectStyles} value={filters.priority} onChange={v => setFilters({...filters, priority: v})} />
                      </FormGroup>
                   </Col>
                   {isEditUser && (
                     <Col md={2}>
                        <FormGroup className="mb-2">
                           <Label className="small fw-bold text-muted mb-1">Assignee</Label>
                           <Select 
                              isClearable 
                              onInputChange={searchTechs} 
                              isLoading={techLoading}
                              options={technicianOptions} 
                              styles={selectStyles} 
                              value={filters.assignee} 
                              onChange={v => setFilters({...filters, assignee: v})} 
                              placeholder="Search..."
                              filterOption={() => true}
                           />
                        </FormGroup>
                     </Col>
                   )}
                </Row>
             </CardBody>
          </Card>

          {/* Main Navigation Tabs */}
          <Nav tabs className="nav-tabs-custom nav-justified mb-4 bg-white shadow-sm rounded">
             <NavItem>
                <NavLink className={classnames({ active: activeMainTab === "1" })} onClick={() => setActiveMainTab("1")} style={{cursor: 'pointer'}}>
                   <i className="bx bx-user-voice me-2 fs-5"></i> Assigned to Me
                </NavLink>
             </NavItem>
             <NavItem>
                <NavLink className={classnames({ active: activeMainTab === "2" })} onClick={() => setActiveMainTab("2")} style={{cursor: 'pointer'}}>
                   <i className="bx bx-send me-2 fs-5"></i> Assigned by Me
                </NavLink>
             </NavItem>
          </Nav>

          <Card className="shadow-sm border-0">
             <CardBody>
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
                   <div className="d-flex gap-2">
                      <Button color={activeCategoryTab === "active" ? "primary" : "outline-secondary"} className="px-4" onClick={() => { setActiveCategoryTab("active"); setActiveStatusTab("any"); }}>
                        Active {activeCategoryTab === "active" && <Badge color="white" className="text-primary ms-2">{totalRecords}</Badge>}
                      </Button>
                      <Button color={activeCategoryTab === "resolved" ? "success" : "outline-secondary"} className="px-4" onClick={() => { setActiveCategoryTab("resolved"); setActiveStatusTab("any"); }}>
                        Resolved {activeCategoryTab === "resolved" && <Badge color="white" className="text-success ms-2">{totalRecords}</Badge>}
                      </Button>
                   </div>
                   <Button color="success" onClick={handleExport} className="box-shadow"><i className="bx bx-export me-1"></i> Export To Excel</Button>
                </div>

                <Nav pills className="bg-light p-1 rounded-pill mb-4 flex-nowrap overflow-auto" style={{ width: 'fit-content' }}>
                   <NavItem>
                      <NavLink className={classnames({ active: activeStatusTab === "any" })} onClick={() => setActiveStatusTab("any")} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>All Statuses</NavLink>
                   </NavItem>
                   {statusList.filter(s => s.category === activeCategoryTab).map(s => (
                      <NavItem key={s.value}>
                         <NavLink className={classnames({ active: activeStatusTab === s.value.toString() })} onClick={() => setActiveStatusTab(s.value.toString())} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>{s.label}</NavLink>
                      </NavItem>
                   ))}
                </Nav>

                <div className="table-responsive">
                   <Table className="table-hover table-centered table-nowrap mb-0 align-middle">
                      <thead className="table-light">
                         <tr>
                            <th>Ticket Info</th>
                            <th>Room/Equip</th>
                            <th>Priority</th>
                            <th>Status</th>
                            <th>Assignee</th>
                            <th>Action</th>
                         </tr>
                      </thead>
                      <tbody>
                         {loading ? (
                             <tr><td colSpan="6" className="text-center py-5"><Spinner color="primary" /></td></tr>
                         ) : filteredTickets.length > 0 ? (
                            filteredTickets.map(t => (
                               <tr key={t.ticketID || t.ticketId}>
                                  <td>
                                      <div className="d-flex align-items-center">
                                          <div className="avatar-xs me-3">
                                              <span className="avatar-title rounded-circle bg-soft-primary text-primary">#</span>
                                          </div>
                                          <div>
                                              <h5 className="text-truncate font-size-14 mb-1 text-primary">#{t.ticketCode}</h5>
                                              <p className="text-muted mb-0 font-size-12">{formatDate(t.createdDate || t.assignedDate)}</p>
                                          </div>
                                      </div>
                                  </td>
                                  <td>
                                      <h5 className="font-size-14 mb-1">{t.roomName || t.equipmentName || "-"}</h5>
                                      <p className="text-muted mb-0 text-truncate" style={{maxWidth: '200px'}}>{t.description}</p>
                                  </td>
                                  <td>{getPriorityBadge(t.priority)}</td>
                                  <td>{getStatusBadge(t.statusId)}</td>
                                  <td>
                                     <div className="d-flex align-items-center">
                                         <i className="bx bx-user-circle fs-4 me-2 text-muted"></i>
                                         <span className="fw-medium">{t.assignedToName || "Unassigned"}</span>
                                     </div>
                                  </td>
                                  <td>
                                     <div className="d-flex gap-2">
                                        <Button size="sm" color="soft-info" title="View" onClick={() => handleActionClick(t, 'view')}><i className="bx bx-show-alt"></i></Button>
                                        {(session.isAdmin || session.userId === t.createdBy) && (
                                           <Button size="sm" color="soft-primary" title="Edit" onClick={() => handleActionClick(t, 'edit')}><i className="bx bx-edit-alt"></i></Button>
                                        )}
                                        {(t.documentCount > 0 || t.hasDocuments) && <Button size="sm" color="soft-dark" title="Documents" onClick={() => handleViewDocument(t)}><i className="bx bx-file"></i></Button>}
                                     </div>
                                  </td>
                               </tr>
                            ))
                         ) : (
                             <tr><td colSpan="6" className="text-center py-5">
                                 <div className="text-muted">
                                     <i className="bx bx-file-blank fs-1 mb-3"></i>
                                     <p>No tickets matching the current filters.</p>
                                 </div>
                             </td></tr>
                         )}
                      </tbody>
                   </Table>
                 </div>
                 
                 {/* Pagination */}
                 {Math.ceil(totalRecords / 10) > 1 && (
                   <div className="d-flex justify-content-between align-items-center mt-3">
                     <div>
                       <p className="text-muted mb-0">
                         Showing {(page - 1) * 10 + 1} to {Math.min(page * 10, totalRecords)} of {totalRecords} entries
                       </p>
                     </div>
                     <div className="pagination-wrapper">
                       <Pagination
                         className="ant-pagination"
                         current={page}
                         total={totalRecords}
                         pageSize={10}
                         onChange={(newPage) => setPage(newPage)}
                         showTitle={false}
                       />
                     </div>
                   </div>
                 )}
              </CardBody>
           </Card>
        </Container>
      </div>

      {/* Update Modal */}
      <Modal isOpen={modal} toggle={() => setModal(!modal)} centered>
        <ModalHeader toggle={() => setModal(!modal)} className="bg-primary text-white" style={{ color: '#fff' }}>
          <span className="text-white">Update Ticket #{selectedTicket?.ticketCode}</span>
        </ModalHeader>
        <ModalBody className="p-4">
          <div className="mb-3">
            <Label className="fw-bold text-primary">Update Status</Label>
            <Input
              type="select"
              value={updateData.statusId}
              onChange={(e) => setUpdateData({ ...updateData, statusId: parseInt(e.target.value) })}
              className="form-select border-primary"
            >
              {statusList.map((s) => {
                const isAdminOrCreator = isUserAdmin() || session.userId === selectedTicket?.createdBy;
                const isAssignee = session.employeeId === selectedTicket?.assignedTo;
                const curr = selectedTicket?.statusId;
                
                let allowed = false;
                
                if (s.value === curr) {
                    allowed = true; 
                } else if (isAdminOrCreator) {
                    if (curr === 1) allowed = [2, 3, 4, 8].includes(s.value);
                    else if (curr === 2 || curr === 5 || curr === 3 || curr === 8) allowed = [1, 2, 3, 4, 7, 8].includes(s.value);
                    else if (curr === 7) allowed = [6, 5, 2, 3, 4, 8].includes(s.value);
                    else if (curr === 6) allowed = [5].includes(s.value);
                } else if (isAssignee && !isAdminOrCreator) {
                    if (curr === 2 || curr === 5) allowed = [3, 7, 8].includes(s.value);
                    else if (curr === 3) allowed = [2, 7, 8].includes(s.value);
                    else if (curr === 8) allowed = [3, 7].includes(s.value);
                }
                
                if (!allowed) return null;
                return <option key={s.value} value={s.value}>{s.label}</option>;
              })}
            </Input>
          </div>
          
          {isUserAdmin() && (
            <div className="mb-3">
              <Label className="fw-bold text-primary">Change Assignee (Optional)</Label>
              <Select
                value={updateData.assignedTo}
                onChange={(opt) => setUpdateData({ ...updateData, assignedTo: opt })}
                options={technicianOptions}
                onInputChange={searchTechs}
                isLoading={techLoading}
                placeholder="Search & Select..."
                styles={selectStyles}
                isClearable
                filterOption={() => true}
              />
            </div>
          )}

          <div className="mb-3">
            <Label className="fw-bold text-primary">Update Remarks</Label>
            <Input
              type="textarea"
              rows="3"
              value={updateData.comment}
              onChange={(e) => setUpdateData({ ...updateData, comment: e.target.value })}
              placeholder="Detail the work done or reason for hold..."
              className="border-primary"
            />
          </div>
        </ModalBody>
        <ModalFooter className="bg-light">
          <Button color="secondary" outline onClick={() => setModal(false)}>Cancel</Button>
          <Button color="primary" onClick={handleUpdateTicket} disabled={loading}>
             {loading ? <Spinner size="sm" /> : "Save Changes"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* View Ticket Details Modal */}
      <Modal isOpen={viewModal} toggle={() => setViewModal(!viewModal)} size="lg" centered>
        <ModalHeader toggle={() => setViewModal(!viewModal)} className="bg-info text-white" style={{ color: '#fff' }}>
          <span className="text-white">Ticket Detail - #{selectedTicket?.ticketCode}</span>
        </ModalHeader>
        <ModalBody className="bg-light p-0">
           <div className="p-4 bg-white border-bottom shadow-sm">
              <Row>
                 <Col md={8}>
                    <h6 className="text-muted text-uppercase fw-bold small mb-2">Issue Description</h6>
                    <p className="fs-5 mb-0 fw-medium text-dark">{selectedTicket?.description}</p>
                 </Col>
                 <Col md={4} className="text-md-end">
                    <div className="mb-2">{selectedTicket && getStatusBadge(selectedTicket.statusId)}</div>
                    <div className="small text-muted">Priority: {selectedTicket && getPriorityBadge(selectedTicket.priority)}</div>
                 </Col>
              </Row>
              <hr className="my-3"/>
              <Row className="small">
                 <Col md={4}>
                    <i className="bx bx-map-pin me-1 text-info"></i>
                    <span className="text-muted">Room/Eq:</span> <span className="fw-bold text-dark ms-1">{selectedTicket?.roomName || selectedTicket?.equipmentName || "-"}</span>
                 </Col>
                 <Col md={4}>
                    <i className="bx bx-user me-1 text-info"></i>
                    <span className="text-muted">Assigned To:</span> <span className="fw-bold text-dark ms-1">{selectedTicket?.assignedToName || "Unassigned"}</span>
                 </Col>
                 <Col md={4} className="text-md-end">
                    <i className="bx bx-calendar me-1 text-info"></i>
                    <span className="text-muted">Logged:</span> <span className="fw-bold text-dark ms-1">{selectedTicket && new Date(selectedTicket.createdDate).toLocaleString()}</span>
                 </Col>
              </Row>
           </div>
           
           <div className="p-4" style={{ maxHeight: '450px', overflowY: 'auto', background: '#f8f9fa' }}>
              <h6 className="text-muted text-uppercase fw-bold small mb-4">
                 <i className="bx bx-comment-detail me-2"></i>Communication History
              </h6>
              {viewLoading ? (
                <div className="text-center py-5">
                   <i className="bx bx-loader bx-spin display-4 text-primary"></i>
                   <p className="mt-2 text-muted">Fetching conversation history...</p>
                </div>
              ) : (
                <div className="chat-history">
                   {ticketLogs.length === 0 ? (
                     <div className="text-center py-5 border rounded bg-white mt-2">
                        <i className="bx bx-chat display-4 text-light mb-2"></i>
                        <div className="text-muted small italic">No comments or activity recorded yet.</div>
                     </div>
                   ) : (
                     ticketLogs.map((log, idx) => {
                        const colorPool = ['primary', 'success', 'warning', 'danger', 'info', 'secondary'];
                        const colorScheme = colorPool[log.actionBy % colorPool.length];
                        const isMe = log.actionBy === session.userId;

                        return (
                           <div key={idx} className={`d-flex mb-4 ${log.comments ? 'flex-column' : 'justify-content-center'}`}>
                              {log.comments ? (
                                <>
                                   <div className={`d-flex align-items-center mb-1 ${isMe ? 'flex-row-reverse' : ''}`}>
                                      <div className="avatar-xs mx-2">
                                         <span className={`avatar-title rounded-circle bg-soft-${colorScheme} text-${colorScheme} fw-bold`}>
                                            {log.actionByName?.substring(0, 1).toUpperCase() || "U"}
                                         </span>
                                      </div>
                                      <div>
                                         <span className={`fw-bold small text-${colorScheme}`}>{log.actionByName}</span>
                                         <span className="text-muted tiny mx-2" style={{fontSize: '0.65rem'}}>{new Date(log.actionDate).toLocaleString([], {hour: '2-digit', minute:'2-digit', month:'short', day:'numeric'})}</span>
                                      </div>
                                   </div>
                                   <div className={`p-3 rounded-lg shadow-sm border-0 ${isMe ? 'bg-white text-dark ms-auto' : 'bg-white text-dark me-auto'} border-start border-4 border-${colorScheme}`} 
                                      style={{ 
                                         maxWidth: '85%', 
                                         width: 'fit-content',
                                         borderRadius: '12px',
                                         boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                      }}>
                                      <div className="fw-medium mb-1">{log.comments}</div>
                                      {log.actionDescription && (
                                         <div className="mt-2 pt-1 border-top small italic text-muted d-flex align-items-center" style={{fontSize: '0.75rem'}}>
                                            <i className="bx bx-info-circle me-1"></i> {log.actionDescription}
                                         </div>
                                      )}
                                   </div>
                                </>
                              ) : (
                                <div className="badge bg-soft-secondary text-muted border-0 px-3 py-1 rounded-pill small fw-normal">
                                   <i className="bx bx-history me-1"></i> {log.actionDescription} - {new Date(log.actionDate).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                </div>
                              )}
                           </div>
                        )
                     })
                   )}
                </div>
              )}
           </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default withTranslation()(GmsMaintenanceTicketsReportPage);
